class customWidget extends HTMLElement {
  constructor() {
    super();
    this.handlePurchaseTypeChange = this.handlePurchaseTypeChange.bind(this);
    this.updateSellingPlanPrice = this.updateSellingPlanPrice.bind(this);

    this.handleChipChange = this.handleChipChange.bind(this);
    this.handleQtyInputChange = this.handleQtyInputChange.bind(this);
  }

  connectedCallback() {
    this.initWidget();
  }

  disconnectedCallback() {
    this.removeWidgetEvents();
  }

  initWidget() {
    this.purchaseTypeInputs = this.querySelectorAll('input[name="purchase-type"]');

    // Qty chips (inside widget)
    this.quantityInputs = this.querySelectorAll('input[name="esa_quantity"]');

    // Selling plan select
    this.sellingPlanSelect = this.querySelector('.custom-select-esa');

    // Find the "real" quantity selector on the page (near ATC)
    // ✅ Update selectors here to match your theme input
    this.qtyInput =
      document.querySelector('input[name="quantity"]') ||
      document.querySelector('.quantity__input') ||
      document.querySelector('[data-quantity-input]');

    // If data-quantity is false, we ignore all qty/discount logic
    this.dataQuantityEnabled = this.getAttribute('data-quantity') !== 'false';

    // Purchase type listeners
    this.purchaseTypeInputs.forEach((input) => {
      input.addEventListener('change', this.handlePurchaseTypeChange);
    });

    // Chip listeners (clicking chip should update real qty input)
    this.quantityInputs.forEach((input) => {
      input.addEventListener('change', this.handleChipChange);
    });

    // Real qty input listener (changing qty input should update prices + chips)
    if (this.qtyInput) {
      this.qtyInput.addEventListener('change', this.handleQtyInputChange);
      this.qtyInput.addEventListener('input', this.handleQtyInputChange);
    }

    // Initial
    const initialSelection = this.querySelector('input[name="purchase-type"]:checked');
    if (initialSelection) this.handlePurchaseTypeChange({ target: initialSelection });

    // Sync chips from initial qty input (optional)
    this.syncChipsFromQtyInput();

    // Calculate initial price
    this.updateSellingPlanPrice();
  }

  removeWidgetEvents() {
    this.purchaseTypeInputs?.forEach((input) => {
      input.removeEventListener('change', this.handlePurchaseTypeChange);
    });

    this.quantityInputs?.forEach((input) => {
      input.removeEventListener('change', this.handleChipChange);
    });

    if (this.qtyInput) {
      this.qtyInput.removeEventListener('change', this.handleQtyInputChange);
      this.qtyInput.removeEventListener('input', this.handleQtyInputChange);
    }
  }

  handlePurchaseTypeChange(event) {
    const selectedValue = event.target.value;
    const isOneTime = selectedValue === 'one-time';

    const sellingPlanSelect = this.querySelector('.custom-select-esa');
    if (sellingPlanSelect) sellingPlanSelect.disabled = isOneTime;

    this.updateSellingPlanPrice();
  }

  handleChipChange(e) {
    if (!this.dataQuantityEnabled) return;

    const qty = parseInt(e.target.value, 10);
    if (!Number.isFinite(qty)) return;

    // Push chip qty into the real qty selector
    if (this.qtyInput) {
      this.qtyInput.value = String(qty);
      // Trigger theme listeners (some themes rely on events)
      this.qtyInput.dispatchEvent(new Event('input', { bubbles: true }));
      this.qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    this.updateSellingPlanPrice();
  }

  handleQtyInputChange() {
    if (!this.dataQuantityEnabled) {
      this.updateSellingPlanPrice();
      return;
    }

    this.syncChipsFromQtyInput();
    this.updateSellingPlanPrice();
  }

  getQtyFromInput() {
    if (!this.dataQuantityEnabled) return 1;

    const qty = this.qtyInput ? parseInt(this.qtyInput.value, 10) : 1;
    return Number.isFinite(qty) && qty > 0 ? qty : 1;
  }

  // Optional UX: select the best matching chip based on current qty input
  syncChipsFromQtyInput() {
    if (!this.dataQuantityEnabled || !this.qtyInput || !this.quantityInputs?.length) return;

    const qty = this.getQtyFromInput();

    // Find chip with value <= qty, closest to qty
    let best = null;
    let bestVal = -Infinity;

    this.quantityInputs.forEach((chip) => {
      const v = parseInt(chip.value, 10);
      if (Number.isFinite(v) && v <= qty && v > bestVal) {
        best = chip;
        bestVal = v;
      }
    });

    // If all chips are > qty, fallback to smallest
    if (!best) {
      let smallest = null;
      let smallestVal = Infinity;
      this.quantityInputs.forEach((chip) => {
        const v = parseInt(chip.value, 10);
        if (Number.isFinite(v) && v < smallestVal) {
          smallest = chip;
          smallestVal = v;
        }
      });
      best = smallest;
    }

    if (best) best.checked = true;
  }

  // Discount is driven by selected chip (based on qty input sync)
  getDiscountFromSelectedChip() {
    if (!this.dataQuantityEnabled) return 0;

    const selectedChip = this.querySelector('input[name="esa_quantity"]:checked');
    if (!selectedChip) return 0;

    const d = parseFloat(selectedChip.dataset.qtyDiscount || 0);
    return Number.isFinite(d) ? d : 0;
  }

  updateSellingPlanPrice() {
    const noscriptEl = document.getElementById('variant__json');
    if (!noscriptEl) return;

    const variantDataStr = noscriptEl.getAttribute('variant-data');
    if (!variantDataStr) return;

    const variantData = JSON.parse(variantDataStr);
    const sellingPlanId = this.sellingPlanSelect?.value;

    const oneTimeBase = variantData.price || 0;
    const compareAtPrice = variantData.compare_at_price || variantData.price || 0;

    // ✅ quantity now comes from the real qty selector
    const quantity = this.getQtyFromInput();

    // ✅ discount still comes from chips (synced to qty input)
    const qtyDiscount = this.getDiscountFromSelectedChip();
    const discountFactor = this.dataQuantityEnabled ? (1 - qtyDiscount / 100) : 1;

    let subUnitPrice = oneTimeBase;
    if (sellingPlanId && variantData.selling_plan_allocations?.length) {
      const allocation = variantData.selling_plan_allocations.find(
        (alloc) => String(alloc.selling_plan_id) === String(sellingPlanId)
      );
      if (allocation?.price != null) subUnitPrice = allocation.price;
    }

    const oneTimeTotal = (oneTimeBase * discountFactor * quantity) / 100;
    const subTotal = (subUnitPrice * discountFactor * quantity) / 100;
    const compareTotal = (compareAtPrice * quantity) / 100;

    const formatNoDecimals = (value) => `${Math.round(value)} kr`;

    const updateCompareVisibility = (boxEl, saleValue) => {
      if (!boxEl) return;

      const delWrap = boxEl.querySelector('.price del');
      const delSpan = boxEl.querySelector('.price del span');
      if (!delWrap || !delSpan) return;

      delSpan.textContent = formatNoDecimals(compareTotal);
      delWrap.style.display = compareTotal > saleValue ? '' : 'none';
    };

    const subscriptionBox = this.querySelector('.subscription-box.box-style');
    if (subscriptionBox) {
      const salePriceElement = subscriptionBox.querySelector('.sale_price_esa');
      if (salePriceElement) salePriceElement.textContent = formatNoDecimals(subTotal);
      updateCompareVisibility(subscriptionBox, subTotal);
    }

    const oneTimeBox = this.querySelector('.one-time-box.box-style');
    if (oneTimeBox) {
      const salePriceElement = oneTimeBox.querySelector('.sale_price_esa');
      if (salePriceElement) salePriceElement.textContent = formatNoDecimals(oneTimeTotal);
      updateCompareVisibility(oneTimeBox, oneTimeTotal);
    }
  }
}

customElements.define('custom-widget', customWidget);